/* eslint-disable react/jsx-props-no-spreading */ // react-dropzone
import React, { useState, useEffect, useCallback } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cx from 'classnames';
import { tokens } from '@companycam/slab';

import { Text } from './sharedStyles';
import { Button } from '@companycam/slab-web';

import { IMAGE_MAX_SIZE_BYTES } from 'constants/files';

// #region styled-components
const ProgressBarContainer = styled.div`
  position: relative;
  overflow: hidden;
  display: block;
  height: 16px;
  margin: 16px auto;
  background: var(--cc_color_border_default);
  border-radius: 20px;
`;

const ProgressBar = styled.div`
  position: absolute;
  top: 0;
  overflow: hidden;
  display: block;
  height: 100%;
  background-color: var(--cc_color_brand_primary);
  border-radius: 20px 0 0 20px;
  width: ${(props) => `${props.uploadProgress}%`};

  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    overflow: hidden;
    background-image: linear-gradient(
      -45deg,
      rgba(255, 255, 255, 0.2) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.2) 75%,
      transparent 75%,
      transparent
    );
    background-size: 50px 50px;
    border-radius: 20px 0 0 20px;
    -webkit-animation: progress 2s linear infinite;
    animation: progress 2s linear infinite;
  }
`;

const TaskListDropZone = styled.div`
  max-width: 100%;
  margin: 24px 0 0;
  padding: 60px;
  text-align: center;
  border: 2px dashed var(--cc_color_border_input_inactive);
  cursor: pointer;
  transition: all ease-in-out 250ms;

  &:hover {
    border: 2px solid var(--cc_color_border_input_active);
  }
`;

const ErrorMessageList = styled.ul`
  margin: ${tokens.size_spacing_m}px 0;
`;

const ErrorMessage = styled.li`
  margin: ${tokens.size_spacing_s}px 0;
  color: var(--cc_color_text_nonessential);
  font-size: ${tokens.size_text_s}px;
  line-height: 1.25;
`;
// #endregion

const messages = defineMessages({
  photosFinished: {
    id: 'Projects.taskList.taskPhotoUploadModal.photosFinished',
    defaultMessage: `Your photos have finished uploading and are now processing. They will appear shortly in your project and field.
    To continue adding photos, click here or drag & drop additional photos`,
    description: 'message displayed when an photos have finished uploading',
  },
  instructions: {
    id: 'Projects.show.photoUploadModal.modalInstructions',
    defaultMessage: `Drag &amp; drop photos here or click to upload to this project.`,
    description: 'instructions to the user',
  },
  uploadCounter: {
    id: 'Projects.show.photoUploadModal.uploading',
    defaultMessage: 'Uploading {count} photos…',
    description: 'message telling the user that the photos are uploading',
  },
  dismissErrors: {
    id: 'Projects.show.photoUploadModal.dismissErrors',
    defaultMessage: 'Dismiss',
    description:
      'Button that allows the user to dismiss the list of upload errors',
  },
  errorMessageTooLarge: {
    id: 'Projects.show.photoUploadModal.errorMessageTooLarge',
    defaultMessage: `Upload failed: {fileName} size is too large. Please select a different file and try again.`,
    description: 'Error message for files over the maximum allowed limit',
  },
  errorMessageWrongFormat: {
    id: 'Projects.show.photoUploadModal.errorMessageWrongFormat',
    defaultMessage:
      'Upload failed: {fileName} format is not accepted. Please select a different file and try again.',
    description: 'Error message for files with format that is not accepted',
  },
  errorMessageExceedLimit: {
    id: 'Projects.show.photoUploadModal.errorMessageExceedLimit',
    defaultMessage:
      'Upload failed: A maximum of 5 reference images are allowed. Please try again.',
    description:
      'Error message for user trying to upload over the maximum amount of allowed files',
  },
});

const DragAndDropUploader = ({
  uploader,
  maxFileSizeBytes = IMAGE_MAX_SIZE_BYTES,
  onFinishedCallback = () => {},
  totalUploadedFiles = 0,
  maxReferencePhotoUploadCount = Infinity,
}) => {
  const [files, setFiles] = useState([]);
  const [finished, setFinished] = useState(0);
  const [errorAlert, setErrorAlert] = useState([]);
  const [uploading, setUploading] = useState(false);
  const intl = useIntl();

  const onDrop = async (acceptedFiles, rejectedFiles) => {
    setErrorAlert([]);
    const formatErrorMessageDropzoneRejection = ({ file }) => {
      if (file.size > maxFileSizeBytes) {
        return intl.formatMessage(messages.errorMessageTooLarge, {
          fileName: file.name,
        });
      }
      return intl.formatMessage(messages.errorMessageWrongFormat, {
        fileName: file.name,
      });
    };

    const uploadable = [...files, ...acceptedFiles];
    const lineSize = uploadable.length;
    const maxAllowed = maxReferencePhotoUploadCount - totalUploadedFiles;

    const allRejectedFiles = rejectedFiles.map(
      formatErrorMessageDropzoneRejection,
    );

    if (lineSize > maxAllowed) {
      allRejectedFiles.unshift(
        intl.formatMessage(messages.errorMessageExceedLimit),
      );
    }
    if (allRejectedFiles.length) {
      setErrorAlert([...allRejectedFiles]);
      return;
    }

    if (finished > uploadable.length) {
      setFinished(0);
    }

    setFiles(uploadable);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: true,
    maxSize: maxFileSizeBytes,
    onDrop,
  });

  const uploadPhoto = useCallback(
    async (file) => {
      setUploading(true);
      const success = await uploader(file);
      if (success) setFinished((f) => f + 1);
      else files.splice(finished, 1);
      setUploading(false);
    },
    [files, finished, uploader],
  );

  const cancelPendingUploads = () => {
    setFinished(0);
    setFiles([]);
  };

  useEffect(() => {
    if (finished && files.length === finished) {
      onFinishedCallback(files);
      setFiles([]);
    }
  }, [finished, files, onFinishedCallback, errorAlert]);

  useEffect(() => {
    if (files.length && finished < files.length && !uploading) {
      uploadPhoto(files[finished]);
    }
  }, [files, finished, uploadPhoto, uploading]);

  useEffect(() => {
    return cancelPendingUploads();
  }, []);

  const renderStatus = () => {
    if (finished > files.length) {
      return (
        <Text className="instructions">
          {intl.formatMessage(messages.photosFinished)}
        </Text>
      );
    }
    if (files.length === 0) {
      return (
        <Text className="instructions">
          {intl.formatMessage(messages.instructions)}
        </Text>
      );
    }

    const uploadProgress = Math.round(100 * (finished / files.length));

    return (
      <div>
        <ProgressBarContainer>
          <ProgressBar uploadProgress={uploadProgress} />
        </ProgressBarContainer>
        <Text className="upload-status">
          {intl.formatMessage(messages.uploadCounter, { count: files.length })}
        </Text>
      </div>
    );
  };

  const taskListDropzoneClasses = cx('task-list-drop-zone', {
    'task-list-drop-zone-active': isDragActive,
  });

  return (
    <>
      <TaskListDropZone
        {...getRootProps()}
        name="task-list-drop-zone"
        className={taskListDropzoneClasses}
      >
        <input {...getInputProps()} />
        {renderStatus()}
      </TaskListDropZone>
      {errorAlert.length > 0 ? (
        <>
          <ErrorMessageList>
            {errorAlert.map((err) => (
              <ErrorMessage key={err}>{err}</ErrorMessage>
            ))}
          </ErrorMessageList>
          <Button
            size="small"
            onClick={(e) => {
              e.preventDefault();
              setErrorAlert([]);
            }}
          >
            {intl.formatMessage(messages.dismissErrors)}
          </Button>
        </>
      ) : null}
    </>
  );
};

DragAndDropUploader.propTypes = {
  maxFileSizeBytes: PropTypes.number,
  onFinishedCallback: PropTypes.func,
  uploader: PropTypes.func.isRequired,
  totalUploadedFiles: PropTypes.number,
  maxReferencePhotoUploadCount: PropTypes.number,
};

export default DragAndDropUploader;
